import { MathUtils } from 'three';

import type { AnnoSourceType, ISeriesPoint, WidgetObject } from '@/types';

import { annoTrackIdGenerator } from './GenerateTrackId';

export type AnnoInstanceItemOptions = {
  trackId?: string;
  attrs?: Record<string, string[]>;
  source?: AnnoSourceType;
  isScaleIndependent?: boolean;
  seriesPoint?: ISeriesPoint;
};
/**
 * 实际标注物，在画布中是可以画出来的
 */
export class AnnoInstanceItem {
  public name: string; // 标注物的唯一标识，就是 anno_v1_Object 的 uuid
  public trackId: string; // 标注物的显示id，形式为递增的整数字符串；一个任务包中，相同的 track_id 表示同一个实体对象
  public label: string; // 所属标签
  public attrs: Record<string, string[]>; // 标注属性数据
  // 服务端格式的对象数据，坐标数值相对于原始文件的坐标系，主要用于适应在不同尺寸的stage上渲染
  public widget: WidgetObject;
  public source: AnnoSourceType; // 在连续帧标注中，'interpolation' 为插值生成的标注对象，'manual' 为关键帧。默认为 'manual'
  public isScaleIndependent: boolean; // 在连续帧中，该标注物的尺寸是否为单帧尺寸，默认为 false
  public seriesPoint?: ISeriesPoint; // 添加点相关信息

  constructor(name: string, label: string, widget: WidgetObject, options?: AnnoInstanceItemOptions) {
    const { source = 'manual', trackId, attrs = {}, isScaleIndependent = false, seriesPoint } = options || {};
    this.name = name;
    this.widget = widget;
    this.label = label;
    this.attrs = attrs;
    this.trackId = trackId ?? annoTrackIdGenerator.createTrackId();
    this.source = source;
    this.isScaleIndependent = isScaleIndependent;
    this.seriesPoint = seriesPoint;
  }

  static createFromObject(obj: Pick<AnnoInstanceItem, keyof AnnoInstanceItem>) {
    const item = new AnnoInstanceItem(obj.name, obj.label, obj.widget, obj);

    return item;
  }

  static createFromMapped(target: AnnoInstanceItem, widget: WidgetObject) {
    return new AnnoInstanceItem(MathUtils.generateUUID(), target.label, widget, {
      trackId: target.trackId,
      attrs: target.attrs,
      source: 'projection',
    });
  }

  static isAnnoInstanceObject(data: Record<string, any>): data is Pick<AnnoInstanceItem, keyof AnnoInstanceItem> {
    if (typeof data.name === 'string' && data.widget && typeof data.widget.name === 'string') {
      return true;
    }
    return false;
  }

  static isEqualWidgetData(a: WidgetObject['data'], b: WidgetObject['data']) {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (Math.abs(a[i] - b[i]) > 1e-5) {
        return false;
      }
    }

    return true;
  }

  setSource(source: AnnoSourceType) {
    this.source = source;
  }

  setIsScaleIndependent(isScaleIndependent: boolean) {
    this.isScaleIndependent = isScaleIndependent;
  }

  public updateWidget(widget: WidgetObject) {
    this.widget = widget;
    if (this.source === 'projection') {
      this.source = 'manual';
    }
    return this;
  }

  public updateLabelAndAttrs(label?: string, attrs?: Record<string, string[]>) {
    if (label) {
      this.label = label;
    }
    if (attrs) {
      this.attrs = attrs;
    }
  }
}
